// This component is used to display a loading switches
// It accepts a promise as a prop and displays a loading spinner until the promise resolves

import { useCallback, useEffect, useState } from 'react';

import { Box, CircularProgress, Switch } from '@mui/material';

interface LoadingSwitchProps {
  checked: boolean;
  disabled?: boolean;
  onClick: (checked: boolean) => Promise<boolean>;
  loading?: boolean;
  size?: 'small' | 'medium';
  inputAriaLabel?: string;
}

export const LoadingSwitch = (props: LoadingSwitchProps) => {
  const { disabled, onClick, size, inputAriaLabel } = props;
  const [loading, setLoading] = useState<boolean>(props.loading ?? false);
  const [checked, setChecked] = useState<boolean>(props.checked ?? false);

  useEffect(() => {
    setChecked(props.checked ?? false);
  }, [props.checked]);

  const handleClick = useCallback(async () => {
    setLoading(true);
    try {
      setChecked(await onClick(!checked));
    } finally {
      // A rejected promise must not leave the spinner on screen forever.
      setLoading(false);
    }
  }, [onClick, checked]);

  const progressSize = size === 'small' ? 16 : 24;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {loading ? (
        <CircularProgress
          size={progressSize}
          sx={size !== 'small' ? { m: '9px' } : undefined}
          disableShrink
        />
      ) : (
        <Switch
          size={size}
          checked={checked}
          onClick={handleClick}
          disabled={disabled}
          slotProps={
            inputAriaLabel != null
              ? { input: { 'aria-label': inputAriaLabel } }
              : undefined
          }
        />
      )}
    </Box>
  );
};
